import { CTABand } from "../../components/site/CTABand";
import { ClipboardList, MapPinned, FileText } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { NetworkCanvas } from "../../components/NetworkCanvas";
import { EnquiryForm, ContactAside } from "../../components/EnquiryForm";
import { PriorityAssessment } from "../../components/PriorityAssessment";
import { Reveal, MaskedLine, FadeIn, Stagger, StaggerItem } from "../../components/motion/Reveal";

const STEPS = [
  { icon: ClipboardList, title: "Share your requirement", text: "Fill the short form — solution area and a few lines about your project. No budgets, no questionnaires." },
  { icon: MapPinned, title: "We assess your site", text: "Our engineers visit your premises anywhere in Kerala — physical layout, wireless, power and existing systems." },
  { icon: FileText, title: "Receive a clear proposal", text: "A documented design with options, bill of quantities and phasing — explained in plain language." },
];

export default function Consultation() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="consultation-page">
      <SiteHeader />
      <section className="relative overflow-hidden pt-20 sm:pt-24 lg:pt-36 pb-16 sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_60%,#060d1a_100%)]" />
        <NetworkCanvas skyline={false} className="absolute inset-0 w-full h-full opacity-40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(6,13,26,0.8)_100%)]" />
        <div className="relative nx-wrap">
          <FadeIn delay={0.15}>
            <div className="flex items-center gap-4 mb-5">
              <span className="h-px w-12 bg-nx-gold/70" />
              <span className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-nx-goldlight">Site Assessment · Consultation</span>
            </div>
          </FadeIn>
          <h1 className="font-marcellus text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.06] max-w-3xl" data-testid="consultation-title">
            <MaskedLine delay={0.3}>Start with a</MaskedLine>
            <MaskedLine delay={0.45} innerClassName="text-nx-goldlight">site assessment.</MaskedLine>
          </h1>
          <FadeIn delay={0.75}>
            <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed" data-testid="consultation-subtitle">
              Every dependable infrastructure project begins on site — not in a catalogue. Tell us where and roughly what; our engineers handle the rest.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-nx-navy2 border-y border-white/10">
        <div className="nx-wrap">
          <Stagger className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={s.title}>
                  <div className="h-full bg-nx-navy2 p-8" data-testid={`consultation-step-${i}`}>
                    <div className="flex items-center justify-between mb-6">
                      <Icon size={24} className="text-nx-goldlight" strokeWidth={1.5} />
                      <span className="font-marcellus text-nx-gold/50 text-lg">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="font-marcellus text-lg text-white mb-3">{s.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.6fr_1fr] gap-14">
          <div>
            <Reveal>
              <h2 className="font-marcellus text-3xl sm:text-4xl text-white mb-3">Request your assessment.</h2>
              <p className="text-base text-slate-400 mb-10 max-w-lg leading-relaxed">Serving businesses, institutions, industries, government organisations and communities across all fourteen Kerala districts.</p>
            </Reveal>
            <Reveal delay={0.15}>
              <EnquiryForm tone="dark" source="consultation" buttonLabel="Request a Site Assessment" />
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10">
                <PriorityAssessment />
              </div>
            </Reveal>
          </div>
          <div className="lg:pt-16">
            <Reveal delay={0.2}>
              <div className="border border-white/10 bg-white/[0.03] p-8">
                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Prefer to reach us directly?</p>
                <ContactAside tone="dark" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
